import React, { useEffect, useState, useMemo } from "react";
import { Form, Modal } from "react-bootstrap";
import { Box, Card, CardContent, Typography, Divider, List, ListItem, ListItemButton, IconButton, Slide, Pagination } from "@mui/material";
import { connect } from "react-redux";
import { getTodayOrders, OrderCancelApi, OrderDetailsPrintPDF } from "./POSActions";
import { GlobalSpinner } from "../../../../global/globalFunctions";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PrintIcon from "@mui/icons-material/Print";
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from "@mui/icons-material/Refresh";
import SinglePageDashbord from "../../drawer/SinglePageDashbord";
// import _ from "lodash";


const STATUS = {
  Completed: { color: "#00c896", bg: "#e6faf5", dot: "#00c896" },
  Pending: { color: "#f59e0b", bg: "#fffbeb", dot: "#f59e0b" },
  Cancelled: { color: "#f43f5e", bg: "#fff1f3", dot: "#f43f5e" },
  default: { color: "#94a3b8", bg: "#f1f5f9", dot: "#94a3b8" },
};
const statusStyle = (s) => STATUS[s] || STATUS.default;
function StatusPill({ status, large }) {
  const s = statusStyle(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: large ? "5px 14px" : "3px 10px",
      borderRadius: 999,
      background: s.bg,
      color: s.color,
      fontSize: large ? "0.85rem" : "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.03em",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

const ITEMS_PER_PAGE = 10;

const ViewOrder = (props) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const fmtNum = (n, pad = 3) => (n ?? 0).toString().padStart(pad, "0");

  const handleCancelOrder = () => {
    props.OrderCancelApi(selectedOrder?._id, { orderCancelReason: cancelReason });
    setShowCancelModal(false);
    setCancelReason("");
  };

  useEffect(() => {
    props.getTodayOrders();
  }, []);

  // Filter orders by search query
  const filteredOrders = useMemo(() => {
    return (props.searchTodayOrdersDataList || []).filter((order) => {
      const search = searchQuery.toLowerCase();
      const orderNumber = `ORD-${order?.number?.toString()?.padStart(3, "0")}`.toLowerCase();
      const customerName = order?.customer?.name?.toLowerCase() || "";
      const orderDate = order?.orderDate || "";
      return (
        orderNumber.includes(search) || customerName.includes(search) || orderDate.includes(search)
      );
    });
  }, [props.searchTodayOrdersDataList, searchQuery]);

  // Pagination math
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * ITEMS_PER_PAGE;
  const paginatedOrders = filteredOrders.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Clamp page if data shrinks (e.g. after refresh or cancel)
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  return (
    <>
      <SinglePageDashbord header='Order View' />

      {props.isFetchingTodayOrdersData ? (
        <GlobalSpinner />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: "calc(100vh - 64px)",
            overflow: "hidden",
            bgcolor: "#f3f6fb",
          }}
        >
          {/* Left Panel */}
          <Box
            sx={{
              width: { xs: "100%", md: selectedOrder ? "38%" : "100%" },
              transition: "width 0.3s ease",
              bgcolor: "white",
              boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
              borderRadius: { md: "12px 0 0 12px" },
              overflowY: "auto",
              height: "100%",
              p: 2,
              m: { md: 2 },
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#1976d2",
                  fontWeight: 600,
                }}
              >
                <ShoppingBagIcon /> Orders
              </Typography>

              <IconButton
                color="primary"
                onClick={() => { props.getTodayOrders(); setSelectedOrder(null); setCurrentPage(1); }}
                sx={{
                  bgcolor: "#e3f2fd",
                  "&:hover": { bgcolor: "#bbdefb" },
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>

            <Form.Control
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                marginBottom: "12px",
                borderRadius: "8px",
                borderColor: "#ccc",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              }}
            />

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <List>
                {paginatedOrders.map((order) => (
                  <ListItem
                    key={order._id}
                    disablePadding
                    sx={{
                      mb: 1,
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: selectedOrder?._id === order._id ? "0 0 0 2px #1976d2 inset" : "0 2px 6px rgba(0,0,0,0.05)",
                    }}
                  >
                    <ListItemButton
                      onClick={() => setSelectedOrder(order)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        bgcolor: selectedOrder?._id === order._id ? "#e3f2fd" : "white",
                      }}
                    >
                      <Box>
                        <Typography fontWeight="600"> ORD-{order?.number.toString().padStart(3, "0")} </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          {order?.customer?.name || ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary"> {moment(order?.orderDate).format("DD-MM-YYYY")} </Typography>
                      </Box>
                      <StatusPill status={order?.status} large />
                    </ListItemButton>
                  </ListItem>
                ))}

                {paginatedOrders.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8", fontFamily: "'DM Sans', sans-serif", fontSize: "0.88rem" }}>
                    No orders found
                  </Box>
                )}
              </List>
            </Box>

            {/* Pagination footer */}
            {filteredOrders.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  pt: 1.5,
                  mt: 1,
                  borderTop: "1px solid #eef1f6",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", fontFamily: "'DM Sans', sans-serif", fontSize: "0.78rem" }}
                >
                  Showing {startIdx + 1}–{Math.min(startIdx + ITEMS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={safePage}
                  onChange={(_, value) => setCurrentPage(value)}
                  color="primary"
                  size="small"
                  shape="rounded"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}

          </Box>

          {/* Right Panel */}
          <Slide direction="left" in={Boolean(selectedOrder)} mountOnEnter unmountOnExit >
            <Box sx={{ width: { xs: "100%", md: "62%" }, transition: "width 0.3s ease", p: 2, m: { md: 2 }, height: "100%", overflowY: "auto", boxSizing: "border-box" }}>
              <Card sx={{ borderRadius: "16px", boxShadow: "0 6px 20px rgba(0,0,0,0.08)", backdropFilter: "blur(8px)" }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}> Order #{selectedOrder?.number.toString().padStart(3, "0")}</Typography>
                    <StatusPill status={selectedOrder?.status} large />
                    <Box>
                      <IconButton onClick={() => setSelectedOrder(null)} style={{ backgroundColor: "#5e5e5e", color: "white" }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
                    {[
                      { label: "Customer", value: selectedOrder?.customer?.name || "—" },
                      { label: "Date", value: moment(selectedOrder?.createdAt).format("DD MMM YYYY, hh:mm A") },
                      { label: "Order Type", value: selectedOrder?.orderType || "—" },
                    ].map(({ label, value }) => (
                      <div key={label} style={{
                        background: "#f7f9fc",
                        borderRadius: 12,
                        padding: "12px 14px",
                        border: "1.5px solid #f0f3f9",
                      }}>
                        <p style={{ margin: 0, fontSize: "0.7rem", color: "#94a3b8", letterSpacing: "0.06em", fontFamily: "'DM Sans', sans-serif" }}>
                          {label.toUpperCase()}
                        </p>
                        <p style={{ margin: "4px 0 0", fontSize: "0.88rem", fontWeight: 600, color: "#0f1e3d", fontFamily: "'DM Sans', sans-serif" }}>
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0", mb: 2 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                      <thead style={{ backgroundColor: "#f8f9fb" }}>
                        <tr>
                          <th style={{ padding: "8px", textAlign: "left" }}>Sr#</th>
                          <th style={{ padding: "8px", textAlign: "left" }}>Product</th>
                          <th style={{ padding: "8px", textAlign: "center" }}>Qty</th>
                          <th style={{ padding: "8px", textAlign: "right" }}>Sale Price</th>
                          <th style={{ padding: "8px", textAlign: "right" }}>Total Sale Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder?.items?.map((item, index) => (
                          <tr key={index} style={{ borderBottom: "1px solid #f0f0f0", backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td style={{ padding: "6px 8px" }}>{index + 1}</td>
                            <td style={{ padding: "6px 8px" }}>
                              {item?.productLabel || ''}
                              {item?.productVariationLabel && (
                                <div style={{ fontSize: "0.75rem", color: "#94a3b8", fontStyle: "italic", marginTop: 2 }}>
                                  {item.productVariationLabel}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: "6px 8px", textAlign: "center" }}>{item?.quantity || ''}</td>
                            <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, }}> {item?.salePrice?.toLocaleString()} </td>
                            <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, }}> {item?.salePrice * item?.quantity} </td>
                          </tr>
                        ))}

                        <tr style={{ backgroundColor: "#f8f9fb", borderTop: "2px solid #e0e0e0" }}>
                          <td colSpan={4} style={{ padding: "5px", textAlign: "right" }}> <b>Sub Total</b> </td>
                          <td style={{ padding: "5px", textAlign: "right", fontWeight: 'bold' }}> {selectedOrder?.subTotal?.toLocaleString()} </td>
                        </tr>

                        <tr style={{ backgroundColor: "#fafafa" }}>
                          <td colSpan={4} style={{ padding: "5px", textAlign: "right" }}> <b>Discount</b></td>
                          <td style={{ padding: "5px", textAlign: "right", fontWeight: 'bold' }}>{selectedOrder?.discount || 0}</td>
                        </tr>

                        <tr style={{ backgroundColor: "#fafafa" }}>
                          <td colSpan={4} style={{ padding: "5px", textAlign: "right" }}> <b>Tax</b></td>
                          <td style={{ padding: "5px", textAlign: "right", fontWeight: 'bold' }}>{selectedOrder?.tax || 0}</td>
                        </tr>

                        <tr style={{ backgroundColor: "#e8f4ff" }}>
                          <td colSpan={4} style={{ padding: "5px 5px", textAlign: "right", fontWeight: 700, fontSize: "1rem" }}>Grand Total</td>
                          <td style={{ padding: "5px 5px", textAlign: "right", fontWeight: 700, fontSize: "1rem", color: "#1976d2", }}>
                            {selectedOrder?.grandTotal?.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {[
                      {
                        label: "Mark Completed",
                        icon: <CheckCircleIcon style={{ fontSize: 17 }} />,
                        bg: "linear-gradient(135deg,#00c896,#00a07a)",
                        disabled: selectedOrder?.status === "Completed" || selectedOrder?.status === "Cancelled",
                      },
                      {
                        label: "Re-Print",
                        icon: <PrintIcon style={{ fontSize: 17 }} />,
                        bg: "linear-gradient(135deg,#6366f1,#4338ca)",
                        disabled: false,
                      },
                      {
                        label: "Print PDF",
                        icon: <PictureAsPdfIcon style={{ fontSize: 17 }} />,
                        bg: "linear-gradient(135deg,#1b6fe8,#0f4c9e)",
                        disabled: false,
                        onClick: () => props.OrderDetailsPrintPDF(selectedOrder),
                      },
                      {
                        label: "Cancel Order",
                        icon: <CancelIcon style={{ fontSize: 17 }} />,
                        bg: "linear-gradient(135deg,#f43f5e,#be123c)",
                        disabled: selectedOrder?.status === "Cancelled",
                        onClick: () => setShowCancelModal(true),
                      },
                    ].map(({ label, icon, bg, disabled, onClick }) => (
                      <button
                        key={label}
                        disabled={disabled}
                        onClick={onClick}
                        style={{
                          all: "unset",
                          cursor: disabled ? "not-allowed" : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 7,
                          padding: "10px 18px",
                          borderRadius: 11,
                          background: disabled ? "#e2e8f0" : bg,
                          color: disabled ? "#94a3b8" : "#fff",
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          boxShadow: disabled ? "none" : "0 4px 14px rgba(0,0,0,0.15)",
                          transition: "transform 0.15s, box-shadow 0.15s",
                          opacity: disabled ? 0.7 : 1,
                        }}
                        onMouseEnter={e => { if (!disabled) e.currentTarget.style.transform = "translateY(-1px)"; }}
                        onMouseLeave={e => { if (!disabled) e.currentTarget.style.transform = "translateY(0)"; }}
                      >
                        {icon}
                        {label}
                      </button>
                    ))}
                  </div>

                </CardContent>
              </Card>
            </Box>
          </Slide>


          <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
            <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
              <Modal.Title style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: "#0f1e3d" }}>
                Cancel Order
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ paddingTop: 12 }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748b", fontSize: "0.88rem", marginBottom: 12 }}>
                Please provide a reason for cancelling <strong>ORD-{fmtNum(selectedOrder?.number)}</strong>.
              </p>
              <textarea
                rows={3}
                placeholder="Enter reason"
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "10px 12px",
                  border: "1.5px solid #e8edf5",
                  borderRadius: 10,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.88rem",
                  resize: "none",
                  outline: "none",
                  color: "#0f1e3d",
                }}
                onFocus={e => e.target.style.borderColor = "#f43f5e"}
                onBlur={e => e.target.style.borderColor = "#e8edf5"}
              />
            </Modal.Body>
            <Modal.Footer style={{ border: "none", paddingTop: 0, margin: '0 auto' }} centered>
              <button
                disabled={!cancelReason}
                onClick={handleCancelOrder}
                style={{
                  all: "unset",
                  cursor: cancelReason ? "pointer" : "not-allowed",
                  padding: "9px 20px", borderRadius: 10,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600, fontSize: "0.85rem",
                  color: "#fff",
                  background: cancelReason ? "linear-gradient(135deg,#f43f5e,#be123c)" : "#e2e8f0",
                  boxShadow: cancelReason ? "0 4px 14px rgba(244,63,94,0.3)" : "none",
                  transition: "opacity 0.15s",
                  opacity: cancelReason ? 1 : 0.6,
                  marginLeft: '10px'
                }}
              >
                Confirm Cancel
              </button>
            </Modal.Footer>
          </Modal>

        </Box>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    getTodayOrders: () => dispatch(getTodayOrders()),
    OrderCancelApi: (orderId, data) => dispatch(OrderCancelApi(orderId, data)),
    OrderDetailsPrintPDF: (orderDetails) => dispatch(OrderDetailsPrintPDF(orderDetails)),
  };
};
const mapStateToProps = (state) => ({
  isFetchingTodayOrdersData: state.POSReducer.isFetchingTodayOrdersData,
  todayOrdersDataList: state.POSReducer.todayOrdersDataList,
  searchTodayOrdersDataList: state.POSReducer.searchTodayOrdersDataList,
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewOrder);